<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { usePayrollStore } from '@/store/payroll-data'
import { useRouter } from 'vue-router'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DTable,
  DModal,
  DEmployeesFilter,
  DLoading,
  FilterLinesIcon,
  Users01Icon,
  ChevronDownIcon,
  DMultiselect
} from 'front-library'
import {
  PayrollReceipt,
  convertArrayToString,
  getProcessCountry,
  getCurrencies,
  getClosedPayrolls,
  getPayrollCurrency,
  getExchangeRatePayroll,
  validateExchangeRate,
  getTemplates,
  getRequest,
  generatePayrollReceipt,
  getEmployees,
  getPayroll,
  getPayrolls,
  getListEmailsRequest,
  sendEmailsPayrollReceipts,
  IExcelNotification,
  generateExcelNotification
} from '../../../core/services/payroll-receipts'
import { useUserStore } from '@/store/user'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')
const acceptQuestion = ref()

const loading = ref(false)
const messageLoading = ref('')

/* Datos Formulario */
const refresh = ref()
const urlFile = ref(null)
const currencyFrom = ref('')
const itemsProcesses = ref<string[]>([])
const itemsPayrolls = ref<any[]>([])
const searchPayroll = ref('')
const searchProcess = ref('')
const itemsCurrencies = ref<string[]>([])
const searchCurrency = ref('')
const itemsClosedPayroll = ref<string[]>([])
const searchClosedPayroll = ref('')
const itemsExchangeRate = ref<string[]>([])
const itemsTemplates = ref<string[]>([])
const searchTemplates = ref('')
const itemsEmployees = ref<string[]>([])
const showSendEmail = ref('')
const isSendEmail = ref(false)
const createPayrollreceipt = ref<PayrollReceipt>()

/* Datos table */
const columns = ref([
  { name: 'employee', title: t('paymentReceipts.list.employee') },
  { name: 'shippingDate', title: t('paymentReceipts.list.shippingDate') },
  {
    name: 'lastOpeningDate',
    title: t('paymentReceipts.list.lastOpeningDate')
  },
  {
    name: 'approvalDate',
    title: t('paymentReceipts.list.approvalDate')
  },
  { name: 'list_errors', title: t('paymentReceipts.list.mistake') }
])
const rows = ref<[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const tableNotification = ref(null)

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  processes: string().required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('incidents.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  processedPayrollId: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  paymentCurrency: object({
    coinId: string().required(t('form.valid.required')),
    exchangeRate: string().matches(/^[0-9]+([.])?([0-9]+)?$/, {
      message: t('form.valid.num')
    })
  }),
  additionalTextA: string().matches(/^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/, {
    excludeEmptyString: true,
    message: t('form.valid.alphanumeric')
  }),
  additionalTextB: string().matches(/^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/, {
    excludeEmptyString: true,
    message: t('form.valid.alphanumeric')
  }),
  additionalTextC: string().matches(/^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/, {
    excludeEmptyString: true,
    message: t('form.valid.alphanumeric')
  }),
  templateId: string().required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any /*paramentPayrollStandard*/>({
  initialValues: {
    additionalTextA: '',
    additionalTextB: '',
    additionalTextC: ''
  },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: payrollId } = useField<string>(ref('payrollId'))
const { value: processes } = useField<string>(ref('processes'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: processedPayrollId } = useField<string[]>(
  ref('processedPayrollId')
)
const { value: coinId } = useField<string>(ref('paymentCurrency.coinId'))
const { value: exchangeRate } = useField<number | string>(
  ref('paymentCurrency.exchangeRate')
)
const { value: templateId } = useField<string>(ref('templateId'))
const { value: additionalTextA } = useField<string>(ref('additionalTextA'))
const { value: additionalTextB } = useField<string>(ref('additionalTextB'))
const { value: additionalTextC } = useField<string>(ref('additionalTextC'))
const { value: employees } = useField<any[]>(ref('employees'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  generatePayload(values)
  if (isSendEmail.value) {
    confirmationWindow()
  } else {
    generatePayrollReceipts(createPayrollreceipt.value)
  }
})

/* Generar payload */
const generatePayload = (values: any) => {
  values.paymentCurrency.exchangeRate === ''
    ? (values.paymentCurrency.exchangeRate = 0)
    : values.paymentCurrency.exchangeRate
  if (isSendEmail.value) {
    createPayrollreceipt.value = {
      code: 'basics/payment-slip',
      module: 'reports',
      subject: t('paymentReceipts.removable'),
      parameters: values
    }
  } else {
    createPayrollreceipt.value = {
      code: 'basics/payment-slip',
      parameters: values
    }
  }
}

/* Generar desprendible */
const generatePayrollReceipts = async (values: any) => {
  loading.value = true
  messageLoading.value = t('paymentReceipts.loading.removable')
  try {
    const response = await generatePayrollReceipt(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Enviar correo desprendible de pago */
const sendEmailPayrollReceipts = async (values: any) => {
  loading.value = true
  messageLoading.value = t('paymentReceipts.loading.removable')
  try {
    await sendEmailsPayrollReceipts(values)
    notifications.addNotification({
      title: t('paymentReceipts.notification.sent'),
      text: t('paymentReceipts.notification.emailNotificationsSuccess'),
      type: 'success',
      time: 10000
    })
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar nominas cerradas */
const getListClosedPayroll = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  if (
    itemsClosedPayroll.value === undefined ||
    itemsClosedPayroll.value.length === 0
  ) {
    itemsEmployees.value = []
    employees.value = []
    processedPayrollId.value = []
  }
  try {
    if (
      payrolls.value != undefined &&
      processes.value != undefined &&
      startDate.value != undefined &&
      endDate.value != undefined
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      itemsClosedPayroll.value = await getClosedPayrolls(
        payrolls.value,
        processes.value,
        startDate.value,
        endDate.value
      )
      if (itemsClosedPayroll.value.length === 0) {
        itemsEmployees.value = []
        employees.value = []
        processedPayrollId.value = []
        rows.value = []
        totalRows.value = 0
        notifications.addNotification({
          title: t('paymentReceipts.notification.noClosedPayrollsFound'),
          text: t('paymentReceipts.notification.noClosedPayrolls'),
          type: 'warning',
          time: 10000
        })
      }
      loading.value = false
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Listar empleados */
const getListEmployees = async (ids: string[]) => {
  let _employees: string[]
  let _closedPayroll: any = itemsClosedPayroll.value.filter((_: any) =>
    ids.includes(_.id)
  )
  if (_closedPayroll.length === 0) {
    itemsEmployees.value = []
    employees.value = []
    processedPayrollId.value = []
    rows.value = []
    totalRows.value = 0
    notifications.addNotification({
      title: t('paymentReceipts.notification.noEmployeesFound'),
      text: t('paymentReceipts.notification.noEmployees'),
      type: 'warning',
      time: 10000
    })
    return
  }
  _employees = _closedPayroll.flatMap((_: any) => _.employees)
  if (_employees.length === 0) {
    notifications.addNotification({
      title: t('paymentReceipts.notification.noEmployeesFound'),
      text: t('paymentReceipts.notification.noEmployees'),
      type: 'warning',
      time: 10000
    })
    return
  }
  getListNotificationsPayroll()
  let _srtEmployees = await convertArrayToString(_employees)
  itemsEmployees.value = await getEmployees('', '', _srtEmployees)
  employees.value = itemsEmployees.value.map((_: any) => _.id)
}

/* Listar monedas */
const getCurrencyPayroll = async () => {
  currencyFrom.value = await getPayrollCurrency(
    storePayroll?.payroll?.id as string
  )
  return currencyFrom.value
}

/* Listar tipos de cambio */
const getListExchangeRatePayroll = async () => {
  itemsExchangeRate.value = await getExchangeRatePayroll(
    storePayroll?.payroll?.id as string
  )
  return itemsExchangeRate.value
}

/* Tipos de cambio */
const exchangeRates = () => {
  if (startDate.value != undefined && endDate.value != undefined) {
    exchangeRate.value = validateExchangeRate(
      itemsExchangeRate.value,
      coinId.value,
      currencyFrom.value,
      startDate.value,
      endDate.value
    ) as number

    exchangeRate.value === 0 ? (exchangeRate.value = '') : exchangeRate.value
  }
}

/* Listar templates */
const getListTemplates = async () => {
  templateId.value = ''
  itemsTemplates.value = []
  let payroll = []
  let company = []
  let country = []
  let listTemplates = []
  listTemplates = await getTemplates(processes.value, payrolls.value)
  payroll = listTemplates.filter(
    (_: any) => _.payrollId === (storePayroll?.payroll?.id as string)
  )
  if (payroll.length > 0) {
    templateId.value = payroll.filter((_: any) => _.isStandard === true)[0]?.id
    return (itemsTemplates.value = payroll)
  }
  company = listTemplates.filter(
    (_: any) => _.companyId === (storePayroll?.payroll?.company.id as string)
  )
  if (company.length > 0) {
    templateId.value = company.filter((_: any) => _.isStandard === true)[0]?.id
    return (itemsTemplates.value = company)
  }
  country = listTemplates.filter(
    (_: any) => _.countryId === (storePayroll.payroll?.country?.id as string)
  )
  if (country.length > 0) {
    templateId.value = country.filter((_: any) => _.isStandard === true)[0]?.id
    return (itemsTemplates.value = country)
  }
}

/* Generar reporte */
const reportRequest = (id: string) => {
  urlFile.value = null
  try {
    refresh.value = setInterval(async () => {
      const response = await getRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('paymentReceipts.modal.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('paymentReceipts.modal.reportNotGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    showError(error)
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'MainReports' })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  if (error.response && error.response.data) {
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  } else {
    notifications.addNotification({
      title: 'Error',
      text: error,
      type: 'error',
      time: 10000
    })
  }
}

/* Mostrar notificaciones */
const showNotifications = () => {
  var iconChevron = document.getElementById('iconChevron')
  var collapseTable = document.getElementById('collapseTable')
  var table = document.getElementById('tableNotifications')
  var showBtn = document.getElementById('showBtn')
  var tableHeight = table!.clientHeight + 54
  if (iconChevron?.children[0].classList.contains('open')) {
    iconChevron.children[0].classList.remove('open')
    showBtn?.classList.remove('show')
    collapseTable!.style.height = '0'
  } else {
    iconChevron!.children[0].classList.add('open')
    showBtn?.classList.add('show')
    collapseTable!.style.height = tableHeight + 'px'
  }
}

/* Cambiar pagina */
const changePerPage = () => {
  setTimeout(() => {
    var collapseTable = document.getElementById('collapseTable')
    var table = document.getElementById('tableNotifications')
    var tableHeight = table!.clientHeight + 54
    var showBtn = document.getElementById('showBtn')
    var iconChevron = document.getElementById('iconChevron')
    collapseTable!.style.height = tableHeight + 'px'
    showBtn?.classList.add('show')
    iconChevron!.children[0].classList.add('open')
  }, 100)
}

/* Generar archivo */
const generateReportNotifications = async () => {
  if (processedPayrollId.value === undefined) return
  loading.value = true
  messageLoading.value = t('paymentReceipts.loading.file')
  try {
    const excelNotification: IExcelNotification = {
      code: 'notifications/payment-slip',
      parameters: {
        processedPayrollId: processedPayrollId.value
      }
    }
    const idProcessRequest = await generateExcelNotification(excelNotification)
    reportRequest(idProcessRequest)
  } catch (error) {
    showError(error)
  }
}

/* Confirmar ventana emergente */
const confirmationWindow = () => {
  showModal.value = true
  typeAlertModal.value = 'question'
  titleModal.value = t('paymentReceipts.modal.sendEmailConfirmation')
  descriptionModal.value = ''
  acceptQuestion.value = acceptQuestion.value = t(
    'paymentReceipts.modal.sendEmail'
  )
  urlFile.value = null
}

/* Listar notificaciones */
const getListNotificationsPayroll = async () => {
  try {
    console.log('entrooo')
    const data = await getListEmailsRequest(
      processedPayrollId.value,
      offset.value,
      perPage.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
  } catch (error) {
    showError(error)
  }
}

onMounted(async () => {
  payrollId.value = storePayroll?.payroll?.id as string
  itemsPayrolls.value = await getPayrolls(store.user?.id as string)
  payrolls.value = itemsPayrolls.value
    .filter((_: any) => _.id === storePayroll.payroll?.id)
    .map((_: any) => _.id)
  itemsProcesses.value = await getProcessCountry()
  itemsCurrencies.value = await getCurrencies()
  await getCurrencyPayroll()
  await getListExchangeRatePayroll()
  showSendEmail.value = await getPayroll(payrollId.value)
  refresh.value = setInterval(() => {
    if (
      processedPayrollId.value !== undefined &&
      processedPayrollId.value.length > 0
    ) {
      void getListNotificationsPayroll()
    }
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="flex flex-col h-full scroll w-full px-3">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      :acceptQuestion="acceptQuestion"
      @closeModal="closeModal"
      @otherMethod="
        sendEmailPayrollReceipts(createPayrollreceipt), (showModal = false)
      ">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('paymentReceipts.viewFile')" />
      </template>
    </DModal>
    <div style="min-height: 100px" class="bg-gray-50 h-28">
      <div class="leading-4 pt-responsive ml-5">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('paymentReceipts.create')" />
      </div>
    </div>
    <div class="flex-grow w-full scroll overflow-y-auto">
      <div class="flex flex-col gap-4 mb-5">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :options="filterOptions(itemsPayrolls, searchPayroll)"
                    :required="true"
                    :error="errors.payrolls"
                    @filter="searchPayroll = $event"
                    @change="getListTemplates(), getListClosedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('paymentReceipts.fields.processes')"
                    v-model="processes"
                    :options="filterOptions(itemsProcesses, searchProcess)"
                    :required="true"
                    :error="errors.processes"
                    @filter="searchProcess = $event"
                    @change="getListTemplates(), getListClosedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('paymentReceipts.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="getListClosedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('paymentReceipts.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="getListClosedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('paymentReceipts.fields.processedPayrolls')"
                    v-model="processedPayrollId"
                    :required="true"
                    :options="
                      filterOptions(itemsClosedPayroll, searchClosedPayroll)
                    "
                    :error="errors.processedPayrollId"
                    @filter="searchClosedPayroll = $event"
                    @change="getListEmployees(processedPayrollId)" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('paymentReceipts.fields.currency')"
                    v-model="coinId"
                    :required="true"
                    :options="filterOptions(itemsCurrencies, searchCurrency)"
                    :error="errors['paymentCurrency.coinId']"
                    @filter="searchCurrency = $event"
                    @change="exchangeRates">
                  </DSelectFieldSearch>
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="number"
                    :label="t('paymentReceipts.fields.exchangeRate')"
                    v-model="exchangeRate"
                    :error="errors.exchangeRate" />
                </div>
                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('paymentReceipts.fields.additionals')" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('paymentReceipts.fields.text1')"
                    v-model="additionalTextA"
                    :error="errors.additionalTextA" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('paymentReceipts.fields.text2')"
                    v-model="additionalTextB"
                    :error="errors.additionalTextB" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('paymentReceipts.fields.text3')"
                    v-model="additionalTextC"
                    :error="errors.additionalTextC" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3 class="text-md font-medium leading-6 text-gray-900">
                  {{ t('paymentReceipts.fields.employees') }}
                </h3>
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end items-start gap-2">
              <DSelectFieldSearch
                class="w-60"
                :placeholder="t('paymentReceipts.fields.defaultTemplate')"
                v-model="templateId"
                :options="filterOptions(itemsTemplates, searchTemplates)"
                :required="true"
                :error="errors.templateId"
                @filter="searchTemplates = $event" />
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                v-if="showSendEmail"
                type="button"
                @click.prevent="onSubmit(), (isSendEmail = true)"
                class="px-5 py-2 font-semibold"
                v-html="t('paymentReceipts.button.sendEmail')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (isSendEmail = false)"
                v-html="t('paymentReceipts.button.print')" />
            </div>
          </template>
        </DCard>
      </div>
      <!-- Notificaciones -->
      <div
        class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-white h-auto min-h-0">
        <main class="p-5 pb-3 flex flex-col">
          <div class="grid grid-cols-4">
            <div class="col-span-4 md:col-span-2 flex items-center">
              <h3
                class="text-lg font-medium leading-6 text-gray-900 flex"
                :class="{ 'cursor-pointer': rows.length != 0 }"
                @click="rows.length != 0 ? showNotifications() : ''">
                {{ t('paymentReceipts.notification.notifications') }}
                <span
                  class="ml-2 bg-blue-600 text-white py-1 px-1.5 text-sm rounded-full w-9 h-7 text-center"
                  style="margin-top: -2px">
                  {{ totalRows }}
                </span>
                <div id="iconChevron">
                  <ChevronDownIcon
                    v-if="rows.length != 0"
                    class="iconChevron ml-2 mt-0.5 w-5" />
                </div>
              </h3>
            </div>
            <div
              v-if="totalRows != 0"
              class="col-span-4 md:col-span-2"
              id="showBtn">
              <div class="flex justify-end">
                <DBtn
                  color="excel"
                  @click.prevent="generateReportNotifications()"
                  v-html="t('form.button.excel')" />
              </div>
            </div>
            <div v-else class="col-span-4 md:col-span-2">
              <div class="flex justify-end">
                <div>
                  <p>{{ t('paymentReceipts.notification.noNotifications') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="rows.length != 0" id="collapseTable">
            <hr class="mt-2 mb-4 border-1 border-gray-300" />
            <DTable
              ref="tableNotification"
              id="tableNotifications"
              :columns="columns"
              :rows="rows"
              :total-rows="totalRows"
              @perPage="
                ;(perPage = $event),
                  changePerPage(),
                  getListNotificationsPayroll()
              "
              @offset="offset = $event">
              <template #list_errors="data: any">
                <div class="my-1.5">
                  <div>
                    <p
                      v-for="error in data.errors"
                      class="text-sm text-gray-500 flex flex-row">
                      {{ error }}
                    </p>
                  </div>
                </div>
              </template>
            </DTable>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>
<style>
.iconChevron {
  transform: rotate(0deg);
  -moz-transition: transform 0.3s linear;
  -ms-transition: transform 0.3s linear;
  -o-transition: transform 0.3s linear;
  -webkit-transition: transform 0.3s linear;
  transition: transform 0.3s linear;
}
.iconChevron.open {
  transform: rotate(180deg);
}
#collapseTable {
  height: 0;
  padding-top: 10px;
  overflow: hidden;
  -moz-transition: all 0.6s ease-in-out;
  -ms-transition: all 0.6s ease-in-out;
  -o-transition: all 0.6s ease-in-out;
  -webkit-transition: all 0.6s ease-in-out;
  transition: all 0.6s;
}
#showBtn {
  opacity: 0;
  transition: opacity 0.6s linear;
}
#showBtn.show {
  opacity: 1;
}
</style>
